/**
 * CameraManager
 * L4-ZOOM-004: PixiJS camera integration for zoom/pan controls
 *
 * Manages camera state and applies transforms to a PixiJS stage container.
 * Provides zoom-toward-cursor behavior and WASD pan support.
 */

import * as PIXI from 'pixi.js';
import {
  Point,
  CameraState,
  CameraConfig,
  DEFAULT_CAMERA_CONFIG,
  INITIAL_CAMERA_STATE,
} from './camera-types';
import { cameraReducer, CameraAction } from './camera-reducer';
import {
  screenToWorld as transformScreenToWorld,
  worldToScreen as transformWorldToScreen,
} from './camera-transforms';

/**
 * CameraManager - Manages zoom and pan for a PixiJS stage
 *
 * Usage:
 * ```typescript
 * const camera = new CameraManager(app.stage);
 * camera.zoomAtPoint(1.5, { x: mouseX, y: mouseY });
 * camera.applyTransform();
 * ```
 */
export class CameraManager {
  private state: CameraState;
  private config: CameraConfig;
  private stage: PIXI.Container;

  /**
   * Create a CameraManager for a PixiJS stage
   *
   * @param stage - The PixiJS stage container to apply transforms to
   * @param config - Optional partial config to override defaults
   */
  constructor(stage: PIXI.Container, config?: Partial<CameraConfig>) {
    this.stage = stage;
    this.state = { ...INITIAL_CAMERA_STATE };
    this.config = { ...DEFAULT_CAMERA_CONFIG, ...config };
  }

  /**
   * Apply current camera state to the PixiJS stage
   *
   * Uses pivot-based transform for zoom-at-point behavior:
   * - Pivot is set to the world-space anchor point
   * - Position is set to the screen-space anchor point
   * - Scale applies the zoom level
   */
  public applyTransform(): void {
    // For zoom-at-point, we need to set:
    // 1. Scale to zoom level
    // 2. Pivot to world position that should stay fixed
    // 3. Position to screen position where that point should appear

    // Calculate the world origin point (0,0 in world coords)
    // and where it should appear on screen
    const worldOrigin: Point = { x: 0, y: 0 };
    const screenOrigin = this.worldToScreen(worldOrigin);

    // Set pivot to 0,0 (world origin)
    this.stage.pivot.set(0, 0);

    // Set position to where world origin should appear on screen
    this.stage.position.set(screenOrigin.x, screenOrigin.y);

    // Set scale to current zoom
    this.stage.scale.set(this.state.zoom, this.state.zoom);
  }

  /**
   * Zoom toward a specific screen point (cursor position)
   *
   * @param targetZoom - Desired zoom level (will be clamped)
   * @param screenPos - Screen position to zoom toward
   */
  public zoomAtPoint(targetZoom: number, screenPos: Point): void {
    this.dispatch({
      type: 'ZOOM_AT_POINT',
      targetZoom,
      screenAnchor: screenPos,
    });
  }

  /**
   * Increase zoom by one step (for button clicks)
   */
  public zoomIn(): void {
    this.dispatch({ type: 'ZOOM_IN' });
  }

  /**
   * Decrease zoom by one step (for button clicks)
   */
  public zoomOut(): void {
    this.dispatch({ type: 'ZOOM_OUT' });
  }

  /**
   * Pan the camera by a delta amount
   *
   * @param deltaX - Horizontal pan delta in pixels
   * @param deltaY - Vertical pan delta in pixels
   */
  public pan(deltaX: number, deltaY: number): void {
    this.dispatch({ type: 'PAN', deltaX, deltaY });
  }

  /**
   * Set the camera pan position directly
   *
   * @param panX - Horizontal pan position in pixels
   * @param panY - Vertical pan position in pixels
   */
  public setPan(panX: number, panY: number): void {
    this.dispatch({ type: 'SET_PAN', panX, panY });
  }

  /**
   * Set the camera zoom level directly (without affecting pan)
   *
   * @param zoom - Zoom level (will be clamped to valid range)
   */
  public setZoom(zoom: number): void {
    this.dispatch({ type: 'SET_ZOOM', zoom });
  }

  /**
   * Reset camera to initial state (1.0x zoom, no pan)
   */
  public reset(): void {
    this.dispatch({ type: 'RESET' });
  }

  /**
   * Fit camera to show specific content bounds centered on screen
   *
   * Uses pivot-based centering (the standard PixiJS pattern for fit-to-view):
   * - Pivot is set to content center (the anchor point for transforms)
   * - Position is set to screen center (where the anchor appears)
   * - Scale is calculated to fit content with margin
   *
   * @param contentBounds - The world-space bounds of content to fit
   * @param screenWidth - Canvas width in pixels
   * @param screenHeight - Canvas height in pixels
   * @param margin - Margin factor (0.1 = 10% margin on each side)
   * @param verticalBias - Shift visual center vertically as fraction of height (0.1 = shift pivot down 10%, showing more lower content)
   */
  public fitToContent(
    contentBounds: { x: number; y: number; width: number; height: number },
    screenWidth: number,
    screenHeight: number,
    margin: number = 0.1,
    verticalBias: number = 0
  ): void {
    // Calculate zoom to fit content with margin
    const marginFactor = 1 - margin * 2; // margin on each side
    const scaleX = (screenWidth * marginFactor) / contentBounds.width;
    const scaleY = (screenHeight * marginFactor) / contentBounds.height;
    const zoom = Math.min(scaleX, scaleY, this.config.maxZoom);
    const clampedZoom = Math.max(zoom, this.config.minZoom);

    // Content center in world coordinates
    const contentCenterX = contentBounds.x + contentBounds.width / 2;
    // Apply vertical bias: positive bias shifts pivot DOWN (world coords),
    // which shifts the VIEW DOWN to show more of the LOWER content
    const contentCenterY = contentBounds.y + contentBounds.height / 2 + (contentBounds.height * verticalBias);

    // Screen center
    const screenCenterX = screenWidth / 2;
    const screenCenterY = screenHeight / 2;

    // Calculate pan values that will center content when applyTransform() is called
    // Formula: screenPos = worldPos * zoom + pan
    // We want: screenCenter = contentCenter * zoom + pan
    // Therefore: pan = screenCenter - contentCenter * zoom
    const panX = screenCenterX - contentCenterX * clampedZoom;
    const panY = screenCenterY - contentCenterY * clampedZoom;

    console.log('[CameraManager] fitToContent:', {
      contentBounds,
      contentCenter: { x: contentCenterX, y: contentCenterY },
      screenCenter: { x: screenCenterX, y: screenCenterY },
      calculatedZoom: zoom,
      clampedZoom,
      pan: { x: panX, y: panY },
      verticalBias
    });

    // Update state with calculated values
    this.state = {
      zoom: clampedZoom,
      panX,
      panY
    };

    // Apply the transform using standard applyTransform (consistent with pan/zoom operations)
    this.applyTransform();
  }

  /**
   * Get current camera state
   */
  public getState(): CameraState {
    return { ...this.state };
  }

  /**
   * Get current zoom level
   */
  public getZoom(): number {
    return this.state.zoom;
  }

  /**
   * Get current camera config
   */
  public getConfig(): CameraConfig {
    return { ...this.config };
  }

  /**
   * Update camera config (e.g., after canvas resize).
   * Typically followed by fitToContent() to re-center the view.
   */
  public updateConfig(partialConfig: Partial<CameraConfig>): void {
    this.config = { ...this.config, ...partialConfig };
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  public screenToWorld(screenPos: Point): Point {
    return transformScreenToWorld(screenPos, this.state);
  }

  /**
   * Convert world coordinates to screen coordinates
   */
  public worldToScreen(worldPos: Point): Point {
    return transformWorldToScreen(worldPos, this.state);
  }

  /**
   * Dispatch an action to update camera state
   */
  private dispatch(action: CameraAction): void {
    this.state = cameraReducer(this.state, action, this.config);
  }
}
